import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import { CardView } from "@/components/v2/CardView";
import { TrendView } from "@/components/v2/TrendView";
import {
  DEFAULT_RECYCLING_TARGET,
  ViewType,
} from "@/components/v2/TrendView/recyclingRate/constants";
import { getPeriodLabelValueFromStartTime } from "@/components/v2/TrendView/utils/formatting";
import {
  getPeriodFilterValue,
  getPreviousYearFilterValue,
} from "@/components/v2/TrendView/utils/timeframe";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { Granularity } from "server/dwhApi/common.js";

import { PortfolioPageContext } from "../../context";
import { GRANULARITY_OPTIONS, PERIOD_OPTIONS } from "./constants";
import {
  getDataWithCurrentMonth,
  getRecyclingRateGraphData,
  getWasteAmountsGraphData,
  getWasteStreams,
} from "./utils";

export const PortfolioTrendView: React.FC = () => {
  const { t, language } = useTrans();
  const { timezone } = timezoneStore();
  const { wasteTypes } = useCommonEntitiesStore();
  const { wasteTypeConfig } = useConfigService();
  const { config: clientConfig, updateConfig: updateClientConfig } =
    useConfig();
  const { selectedBuildingIds } = useContext(PortfolioPageContext);
  const [showCompareYear, setShowCompareYear] = useState(false);

  const [granularity, setGranularity] = useState<Granularity>(
    Granularity.MONTH,
  );
  const [period, setPeriod] = useState<FilterTimeFrameValue>(
    FilterTimeFrameValue.Last12Months,
  );

  const granularityFilter = useMemo(
    () => ({
      options: GRANULARITY_OPTIONS,
      value: granularity,
      onChange: (key: string) => setGranularity(key as Granularity),
    }),
    [granularity],
  );

  const periodFilter = useMemo(
    () => ({
      options: PERIOD_OPTIONS,
      value: period,
      onChange: (key: string) => setPeriod(key as FilterTimeFrameValue),
    }),
    [period],
  );

  const periodFilterValue = useMemo(
    () => getPeriodFilterValue({ period, timezone }),
    [period, timezone],
  );

  const previousYearFilterValue = useMemo(
    () => getPreviousYearFilterValue({ periodFilterValue, period, timezone }),
    [period, periodFilterValue, timezone],
  );

  const { data: periodData, isLoading: isPeriodLoading } =
    trpc.dwh.terminals.getAggregatesPeriods.useQuery(
      {
        ...periodFilterValue,
        timeZone: timezone,
        granularity: granularity,
        terminalIds: selectedBuildingIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            periodFilterValue.startTimeISO,
            periodFilterValue.endTimeISO,
            timezone,
            granularity,
            selectedBuildingIds,
          ].toString(),
        enabled: !!selectedBuildingIds,
      },
    );

  const { data: periodDataComparison, isLoading: isPeriodComparisonLoading } =
    trpc.dwh.terminals.getAggregatesPeriods.useQuery(
      {
        ...previousYearFilterValue,
        timeZone: timezone,
        granularity: granularity,
        terminalIds: selectedBuildingIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            previousYearFilterValue.startTimeISO,
            previousYearFilterValue.endTimeISO,
            timezone,
            granularity,
            period,
            selectedBuildingIds,
          ].toString(),
        enabled:
          !!period &&
          !!selectedBuildingIds &&
          period === FilterTimeFrameValue.Last12Months &&
          granularity === Granularity.MONTH &&
          !!showCompareYear,
      },
    );

  const getPeriodLabelValue = useCallback(
    (startTime: string) =>
      getPeriodLabelValueFromStartTime({
        period,
        granularity,
        startTime,
        language,
        timezone,
      }),
    [granularity, language, period, timezone],
  );

  const dataWithCurrentMonth = useMemo(
    () => getDataWithCurrentMonth({ periodData, period }),
    [periodData, period],
  );

  const buildingTrendFilters = useMemo(
    () => [granularityFilter, periodFilter],
    [granularityFilter, periodFilter],
  );

  const showLoadingState = useMemo(
    () => isPeriodLoading || isPeriodComparisonLoading,
    [isPeriodLoading, isPeriodComparisonLoading],
  );

  const comparisonYearBeforeData = useMemo(
    () =>
      period === FilterTimeFrameValue.Last12Months &&
      granularity === Granularity.MONTH
        ? periodDataComparison
        : undefined,
    [period, granularity, periodDataComparison],
  );

  const recyclingRateData = useMemo(
    () =>
      getRecyclingRateGraphData({
        data: dataWithCurrentMonth,
        getPeriodLabelValue,
        comparisonYearBeforeData,
      }),
    [dataWithCurrentMonth, getPeriodLabelValue, comparisonYearBeforeData],
  );
  const wasteAmountData = useMemo(
    () =>
      getWasteAmountsGraphData({
        data: dataWithCurrentMonth,
        getPeriodLabelValue,
        wasteTypes,
        wasteTypeConfig,
      }),
    [dataWithCurrentMonth, getPeriodLabelValue, wasteTypeConfig, wasteTypes],
  );

  const wasteStreams = useMemo(
    () =>
      getWasteStreams({
        data: dataWithCurrentMonth,
        wasteAmountData,
        wasteTypes,
        wasteTypeConfig,
      }),
    [dataWithCurrentMonth, wasteAmountData, wasteTypes, wasteTypeConfig],
  );

  const target = useMemo(
    () => clientConfig?.recyclingTarget || DEFAULT_RECYCLING_TARGET,
    [clientConfig],
  );

  const updateTarget = useCallback(
    (value: number) => {
      updateClientConfig({
        recyclingTarget: value,
      });
    },
    [updateClientConfig],
  );

  return (
    <CardView
      title="portfolioPage:portfolioTrendTitle"
      filters={buildingTrendFilters}
      isLoading={showLoadingState}
    >
      <TrendView
        period={period}
        granularity={granularity}
        showLoadingState={showLoadingState}
        recyclingRateData={recyclingRateData}
        wasteAmountsData={wasteAmountData}
        wasteAmountsStreams={wasteStreams}
        recyclingRateSubtitle={t(
          "portfolioPage:portfolioTrendRecyclingRateSubtitle",
        )}
        wasteAmountsSubtitle={t(
          "portfolioPage:portfolioTrendWasteAmountsSubtitle",
        )}
        viewType={ViewType.PORTFOLIO}
        target={target}
        updateTarget={updateTarget}
        showCompareYear={showCompareYear}
        setShowCompareYear={setShowCompareYear}
      />
    </CardView>
  );
};
